import { screenshareStopped } from 'features/screenshare/actions';
import { getDisplayMedia } from 'features/user-media/utils/getDisplayMedia';
import { selectLocalUserId } from 'features/users/usersSlice';
import { selectScreenshareStreams } from 'features/layout/features/order/orderSlice';
import { contentBoard } from 'features/content-whiteboard/utils/contentBoardStateManager';
import { store } from 'store/store';
import { logger } from 'utils/logger';
import { FeedMediaState, RTCClient } from 'utils/webrtc/index';
import { BasePublishing } from 'utils/webrtc/publishing';
import { screenshareSimulcastMaxBitrates } from 'utils/webrtc/environment';
import type { MobileScreenshareData } from 'utils/eventBus';
import {
  emitPublishEvent,
  PUBLISH_DEFERRED_EVENT,
  publishDeferredMessage,
  PUBLISH_DEFERRED_RESOLVED_EVENT,
  publishDeferredResolvedMessage,
  PUBLISH_DEFERRED_CANCELLED_EVENT,
  publishDeferredCancelledMessage,
  PUBLISH_SCREENSHARE_RESHARE_EVENT,
  publishScreenshareReshareMessage,
  ScreenshareReshareBranch,
  PUBLISH_SCREENSHARE_STREAM_MISSING_EVENT,
  PUBLISH_SCREENSHARE_STREAM_MISSING_MESSAGE,
} from 'utils/webrtc/publishing/publishTelemetry';

export class ScreensharingFeed extends BasePublishing {
  defaultMediaState: FeedMediaState = {
    enabled: true,
    allowed: true,
    broadcasting: false,
    captured: false,
  };

  mediaStates = {
    video: { ...this.defaultMediaState },
    audio: { ...this.defaultMediaState },
  };

  savedStream: MediaStream | null = null;

  pendingReconnect: boolean = false;

  isReconnecting: boolean = false;

  mobileScreenshareEnabled: boolean = false;

  screenshareDeferredAt: number | null = null;

  constructor() {
    super('screensharing');
  }

  // An audio:'send' descriptor with no captured display-audio track makes janus fall back to
  // getUserMedia and open the microphone. Gate screenshare audio on real display audio.
  private displayStreamHasAudio = () => !!this.savedStream?.getAudioTracks().length;

  getActiveMediaConfig = () => {
    if (this.savedStream) {
      const clonedStream = new MediaStream();

      this.savedStream.getTracks().forEach((track) => clonedStream.addTrack(track.clone()));

      this.mediaController.storedMediaStreams[clonedStream.id] = clonedStream;

      return {
        stream: clonedStream,
        ...(this.displayStreamHasAudio() ? {} : { audio: false as const }),
      };
    }

    if (this.broadcastIntended) {
      emitPublishEvent({
        event: PUBLISH_SCREENSHARE_STREAM_MISSING_EVENT,
        message: PUBLISH_SCREENSHARE_STREAM_MISSING_MESSAGE,
        level: 'warn',
        feedType: this.kind,
        visibility: 'fleet',
        cap: 3,
      });
    }

    return {
      video: false,
      audio: false,
    };
  };

  protected getVideoTrackType = () => 'screen' as const;

  protected getSimulcastMaxBitrates = () => screenshareSimulcastMaxBitrates;

  protected getSendEncodings = () => {
    const enableSimulcast = this.isSimulcastEnabled();

    if (!enableSimulcast) {
      return null;
    }

    const bitrates = this.getSimulcastMaxBitrates();

    const encodings = {
      h: { rid: 'h', active: true, maxBitrate: bitrates.high },
      l: {
        rid: 'l',
        active: enableSimulcast,
        maxBitrate: bitrates.low,
        scaleResolutionDownBy: 3,
      },
    };

    return Object.values(encodings);
  };

  protected isSimulcastEnabled = () => {
    const state = store.getState();
    const screenshares = selectScreenshareStreams(state);
    return screenshares.length > 5;
  };

  captureScreensharingState = () => {
    const { broadcastIntended, savedStream } = this;

    if (broadcastIntended && savedStream) {
      return { broadcastIntended, savedStream };
    }

    return null;
  };

  restoreScreensharingState = ({
    broadcastIntended,
    savedStream,
  }: {
    broadcastIntended: boolean;
    savedStream: MediaStream;
  }) => {
    this.broadcastIntended = broadcastIntended;
    this.savedStream = savedStream;
    this.attachEndedHandlers();
  };

  scheduleReconnectPublish = () => {
    if (!this.broadcastIntended) {
      return;
    }

    this.resetMediaStates();
    this.pendingReconnect = true;
  };

  completeReconnect = () => {
    this.isReconnecting = false;
  };

  startMobileScreenshare = (data: MobileScreenshareData) => {
    const isVideoStreamExist = data.streams.some((stream) => stream.type === 'video');

    data.streams.forEach((stream) => {
      this.mediaStates[stream.type].broadcasting = true;
    });

    if (isVideoStreamExist) {
      this.broadcastIntended = true;
      this.mobileScreenshareEnabled = true;

      RTCClient.setupLocalFeed({
        id: data.feedId,
        private_id: RTCClient.privateId || 0, // TODO check the value
      });
    }

    this.signalingBus.addMessage('start', data.feedId, data.streams);

    this.updateRedux();
  };

  stopMobileScreenshare = (data: MobileScreenshareData) => {
    const isVideoStreamExist = data.streams.some((stream) => stream.type === 'video');

    data.streams.forEach((stream) => {
      this.mediaStates[stream.type].broadcasting = false;
    });

    if (isVideoStreamExist) {
      this.broadcastIntended = false;
      this.mobileScreenshareEnabled = false;
    }

    this.signalingBus.addMessage('stop', data.feedId, data.streams);

    this.updateRedux();
  };

  shareScreen = async () => {
    this.broadcastIntended = true;

    // Publishing connection is torn down during reconnect; defer the attach so
    // attemptPublish resumes it once the connection is re-established.
    if (!RTCClient.mediaServerConnector.connections.publishing) {
      this.screenshareDeferredAt = performance.now();
      emitPublishEvent({
        event: PUBLISH_DEFERRED_EVENT,
        message: publishDeferredMessage('pending-reconnect'),
        level: 'info',
        feedType: this.kind,
        visibility: 'fleet',
        cap: 10,
        attributes: {
          reason: 'pending-reconnect',
          hasSavedStream: !!this.savedStream,
        },
      });
      this.pendingReconnect = true;
      return;
    }

    // Read and clear on every branch, not just A, so a defer superseded by a later
    // call (e.g. cancel-and-reshare) doesn't leak into an unrelated resolution.
    const deferredAt = this.screenshareDeferredAt;
    this.screenshareDeferredAt = null;

    let branch: ScreenshareReshareBranch;
    if (this.pendingReconnect) {
      branch = 'a';
    } else if (this.handle) {
      branch = 'b';
    } else {
      branch = 'c';
    }

    emitPublishEvent({
      event: PUBLISH_SCREENSHARE_RESHARE_EVENT,
      message: publishScreenshareReshareMessage(branch),
      level: 'info',
      feedType: this.kind,
      visibility: 'fleet',
      cap: 10,
      attributes: {
        branch,
        pendingReconnect: this.pendingReconnect,
        hasSavedStream: !!this.savedStream,
        hasHandle: !!this.handle,
      },
    });

    if (branch === 'a') {
      this.cleanupConnection();
      this.mediaStates.audio.enabled = this.displayStreamHasAudio();
      this.attachPlugin();
      this.pendingReconnect = false;
      this.isReconnecting = true;

      // Funnel pairing, not branch distribution: only fires when a deferral was
      // actually outstanding, since pendingReconnect also gets set with no defer
      // by scheduleReconnectPublish.
      if (deferredAt !== null) {
        emitPublishEvent({
          event: PUBLISH_DEFERRED_RESOLVED_EVENT,
          message: publishDeferredResolvedMessage('pending-reconnect'),
          level: 'info',
          feedType: this.kind,
          visibility: 'fleet',
          cap: 10,
          attributes: {
            reason: 'pending-reconnect',
            msSinceDefer: performance.now() - deferredAt,
          },
        });
      }
    } else if (branch === 'b') {
      this.resetMediaStates();
      // TODO: Maybe reset negotioation meta in here?; or maybe reset media states is not necessary

      const hasSystemAudio = this.displayStreamHasAudio();
      this.mediaStates.audio.enabled = hasSystemAudio;

      await this.requestPublish({
        intent: { video: 'send', audio: hasSystemAudio ? 'send' : undefined },
      });

      this.pendingReconnect = false;
    } else {
      this.attachPlugin();
    }
  };

  stopScreenshare = async (notifySignaling: boolean = true) => {
    this.broadcastIntended = false;

    // An explicit stop must not be resurrected once the connection returns, and an
    // uncounted cancellation inflates the deferred-minus-resolved gap.
    this.pendingReconnect = false;
    const deferredAt = this.screenshareDeferredAt;
    this.screenshareDeferredAt = null;

    if (deferredAt !== null) {
      emitPublishEvent({
        event: PUBLISH_DEFERRED_CANCELLED_EVENT,
        message: publishDeferredCancelledMessage('pending-reconnect'),
        level: 'info',
        feedType: this.kind,
        visibility: 'fleet',
        // Matches its two funnel partners: a tighter cap on one leg makes
        // deferred-minus-resolved-minus-cancelled report phantom losses.
        cap: 10,
        attributes: {
          reason: 'pending-reconnect',
          msSinceDefer: performance.now() - deferredAt,
        },
      });
    }

    this.savedStream?.getTracks().forEach((track) => {
      track.stop();
    });

    this.savedStream = null;

    contentBoard.localScreenshareState = [];

    if (this.handle?.feedId) {
      if (notifySignaling) {
        this.signalingBus.addMessage(
          'stop',
          this.handle.feedId,
          Object.values(this.handle.streams)
        );
      }

      const userId = selectLocalUserId(store.getState());

      store.dispatch(
        screenshareStopped({
          id: userId,
          feedId: this.handle.feedId,
          streams: Object.values(this.handle.streams),
        })
      );
    }

    try {
      await this.requestPublish({
        intent: { video: 'remove', audio: 'remove' },
      });
    } catch (e) {
      // do nothing, it's ok
    }

    this.cleanupStreams();

    if (this.mediaStates.video.broadcasting) {
      this.stopVideoBroadcasting();
    } else {
      this.resetMediaStates('video');
    }

    if (this.mediaStates.audio.broadcasting) {
      this.stopAudioBroadcasting();
    } else {
      this.resetMediaStates('audio');
    }

    this.updateRedux();
  };

  toggleScreenshare = async () => {
    if (this.broadcastIntended) {
      await this.stopScreenshare();
    } else {
      await this.shareScreen();
    }
  };

  onMediaState = async (medium: 'audio' | 'video', on: boolean, mid?: string) => {
    logger
      .remote()
      .debug(`Janus ${on ? 'started' : 'stopped'} receiving our ${medium} (mid=${mid})`);

    const feed = this.handle;

    if (!feed) {
      return;
    }

    if (on && !this.broadcastIntended) {
      return;
    }

    feed.streams = feed.streams || {};

    if (on) {
      RTCClient.resetPublishOptions();

      if (mid) {
        if (medium === 'audio' && this.negotiationMeta.pendingAudioBroadcastState !== null) {
          logger.warn(
            'ON_MEDIA_STATE tweaking audio with pending state...',
            this.negotiationMeta.pendingAudioBroadcastState
          );

          if (this.negotiationMeta.pendingAudioBroadcastState) {
            feed.videoroom.unmuteAudio(mid);
          } else {
            feed.videoroom.muteAudio(mid);
          }

          this.negotiationMeta.pendingAudioBroadcastState = null;
        }

        feed.streams[mid] = { type: medium, mid, on: true };
      }

      if (!feed.connected && !feed.connecting) {
        feed.connecting = true;
      }
    } else if (mid && feed.streams[mid]) {
      feed.streams[mid].on = false;
    }

    const shouldSendUpdate = on ? !this.mediaStates[medium].captured : false;

    this.mediaStates[medium].broadcasting = on;
    this.mediaStates[medium].captured = on;

    if (mid && shouldSendUpdate) {
      this.signalingBus.addMessage(on ? 'start' : 'stop', feed.feedId, [{ type: medium, mid }]);
    }

    this.updateRedux();
  };

  cleanupStreams = () => {
    Object.keys(this.mediaController.streams).forEach((id) => {
      this.mediaController.removeStream(id);
    });

    this.mediaController.clearStoredMediaStreams();
  };

  cleanupConnection = () => {
    logger.debug('Screensharing Feed: cleaning up connection');
    const feed = this.handle;
    this.resetMediaStates();

    this.handleCleanup();

    this.reconnectionReset();

    if (!feed) {
      return;
    }

    if (feed.feedId) {
      RTCClient.disconnectFeed(feed.feedId);
    }

    feed.videoroom.hangup();
  };

  attachEndedHandlers = () => {
    this.savedStream?.getTracks().forEach((track) => {
      track.onended = () => {
        if (this.broadcastIntended) {
          this.stopScreenshare();
        }
      };
    });
  };

  saveStream = async () => {
    this.savedStream = await getDisplayMedia();
    this.mediaStates.audio.enabled = this.displayStreamHasAudio();
    this.attachEndedHandlers();
  };
}
